import React, { useState, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import * as Sentry from "@sentry/react";
import axios from "axios";

// Import All Components
import { LoginPage } from "./components/LoginPage";
import { CompanyDetailsPage } from "./components/CompanyDetailsPage";
import VideoCardScanner from "./components/VideoCardScanner/VideoCardScanner";
import CardHistoryPage from "./components/CardHistoryPage";
import { UserCreationPage } from "./components/UserCreationPage";
import { ImpersonationBar } from "./components/ImpersonationBar";
import Navbar from "./components/Navbar";
import ResponsiveLayout from "./components/ResponsiveLayout";
import { SnackbarProvider } from "./context/Snackbar";
import TeamManagementPage from "./components/TeamManagementPage";
import TemplatePage from "./components/TemplatePage";

const theme = createTheme({
  palette: {
    primary: { main: "#1976d2" },
    secondary: { main: "#dc004e" },
    background: { default: "#f5f5f5" },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 500 },
  },
  components: {
    MuiButton: {
      styleOverrides: { root: { textTransform: "none", borderRadius: 8 } },
    },
    MuiCard: { styleOverrides: { root: { borderRadius: 12 } } },
  },
});

function AppRoutes() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => localStorage.getItem("isAuthenticated") === "true"
  );
  const [companyData, setCompanyData] = useState(() => {
    const stored = localStorage.getItem("companyData");
    return stored ? JSON.parse(stored) : null;
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [isSessionLoading, setSessionLoading] = useState(true);

  const API_BASE = import.meta.env.VITE_API_BASE;

  const fetchInitialData = async () => {
    setSessionLoading(true);
    try {
      const sessionRes = await axios.post(
        `${API_BASE}/get-session-data`,
        {},
        {
          withCredentials: true,
        }
      );
      const sessionResult = sessionRes.data;

      if (sessionResult.success) {
        setCurrentUser(sessionResult.data.currentUser);
        if (sessionResult.data.allUsers)
          setAllUsers(sessionResult.data.allUsers);

        // FIX: Wrap the company fetch in its own try/catch
        try {
          const companyRes = await axios.get(`${API_BASE}/get-company-data`, {
            withCredentials: true,
          });
          const companyResult = companyRes.data;

          if (companyResult.success) {
            const company = {
              username: companyResult.company.username,
              companyName: companyResult.company.company_name,
              role: companyResult.company.role,
              companyUrl: companyResult.company.company_url,
              description: companyResult.company.description,
            };
            setCompanyData(company);
            localStorage.setItem("companyData", JSON.stringify(company));
          } else {
            setCompanyData(null);
            localStorage.removeItem("companyData");
          }
        } catch (companyError) {
          // If this fails (like a 404), DO NOT log the user out. 
          // Just assume they haven't filled out their company details yet.
          console.warn("Company data not found or failed to load.", companyError.message);
          setCompanyData(null);
          localStorage.removeItem("companyData");
        }

      } else {
        await handleLogout(false); // Force logout, no navigate
      }
    } catch (error) {
      console.error("Failed to fetch initial session data:", error);
      // Sentry: Only log server/network errors
      if (error.response && error.response.status >= 500) {
        Sentry.captureException(error, {
          tags: { section: "app", source: "fetchInitialData" },
          extra: {
            status: error.response.status,
            response: error.response.data,
          },
        });
      }
      await handleLogout(false); // Only log out if session API fails entirely
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchInitialData();
    } else {
      setSessionLoading(false);
    }
  }, [isAuthenticated]);

  const handleLogin = (success) => {
    if (success) {
      localStorage.setItem("isAuthenticated", "true");
      setIsAuthenticated(true);
    }
  };

  const handleCompanySubmit = (data) => {
    setCompanyData(data);
    localStorage.setItem("companyData", JSON.stringify(data));
    navigate("/scanner");
  };

  const handleLogout = async (doNavigate = true) => {
    if (currentUser?.original_user_id) await handleStopImpersonation(false);
    try {
      await axios.post(`${API_BASE}/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout failed:", error);
      if (error.response && error.response.status >= 500) {
        Sentry.captureException(error, {
          tags: { section: "app", source: "logout" },
          extra: {
            status: error.response.status,
            response: error.response.data,
          },
        });
      }
    }
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("companyData");
    setIsAuthenticated(false);
    setCompanyData(null);
    setCurrentUser(null);
    setAllUsers([]);
    if (doNavigate) navigate("/login");
  };

  const handleImpersonate = async (userIdToImpersonate) => {
    if (!userIdToImpersonate) return;
    try {
      await axios.post(
        `${API_BASE}/impersonate-user`,
        { user_id_to_impersonate: userIdToImpersonate },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );
      window.location.reload();
    } catch (error) {
      console.error("Impersonation failed:", error);
      // Only log server errors (status >= 500) to Sentry
      if (error.response && error.response.status >= 500) {
        Sentry.captureException(error, {
          tags: { section: "app", source: "handleImpersonate" },
          extra: {
            status: error.response.status,
            response: error.response.data,
            endpoint: `${API_BASE}/impersonate-user`,
            userIdToImpersonate,
          },
        });
      }
    }
  };

  const handleStopImpersonation = async (reload = true) => {
    try {
      await fetch(`${API_BASE}/stop-impersonation`, {
        method: "POST",
        credentials: "include",
      });
      if (reload) window.location.reload();
    } catch (error) {
      console.error("Failed to stop impersonation:", error);
    }
  };

  if (isSessionLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {isAuthenticated && currentUser && (
        <ResponsiveLayout
          currentUser={currentUser}
          allUsers={allUsers}
          onImpersonate={handleImpersonate}
          onLogout={handleLogout}
        />
      )}
      <Box sx={{ height: "64px" }}></Box>
      {isAuthenticated && currentUser?.original_user_id && (
        <ImpersonationBar
          impersonatedUser={currentUser.impersonating_as}
          onStop={handleStopImpersonation}
        />
      )}
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              companyData ? (
                <Navigate to="/scanner" />
              ) : (
                <Navigate to="/company-details" />
              )
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/login"
          element={
            !isAuthenticated ? (
              <LoginPage onLogin={handleLogin} />
            ) : (
              <Navigate to="/scanner" />
            )
          }
        />
        <Route
          path="/signup"
          element={
            !isAuthenticated ? (
              <LoginPage onLogin={handleLogin} />
            ) : (
              <Navigate to="/scanner" />
            )
          }
        />
        <Route
          path="/create-user"
          element={
            isAuthenticated && currentUser?.is_super_admin ? (
              <UserCreationPage />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/company-details"
          element={
            isAuthenticated ? (
              <CompanyDetailsPage
                onSubmit={handleCompanySubmit}
                onLogout={handleLogout}
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/scanner"
          element={
            isAuthenticated && companyData ? (
              <VideoCardScanner
                companyData={companyData}
                onLogout={handleLogout}
                currentUser={currentUser}
              />
            ) : isAuthenticated ? (
              <Navigate to="/company-details" />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/history"
          element={
            isAuthenticated ? (
              <CardHistoryPage
                onLogout={handleLogout}
                currentUser={currentUser}
                companyData={companyData}
              />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/team"
          element={
            isAuthenticated ? <TeamManagementPage /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/templates"
          element={
            isAuthenticated ? <TemplatePage /> : <Navigate to="/login" />
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider>
        <Router>
          <AppRoutes />
        </Router>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;